import { useSelector } from "react-redux";

/**
 * Custom hook to get certificate status
 * Returns consistent certificate status across all components
 */
export const useCertificateStatus = () => {
  const user = useSelector((state) => state.user);
  const certificate = user?.certificate_safety;

  // Helper function to compare dates (ignoring time)
  const isDateExpired = (expirationDateString) => {
    if (!expirationDateString) return true;

    const expirationDate = new Date(expirationDateString);
    const today = new Date();

    // Reset time to start of day for accurate date comparison
    expirationDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    // Certificate is expired if expiration date is before or equal to today
    return expirationDate <= today;
  };

  // Get certification status
  const getCertificationStatus = () => {
    // TEMPORARY LOGIC: If user is visible, assume certificate is approved
    // This bypasses the admin approval process for now
    if (user?.visible) {
      // Still check if certificate exists and is not expired
      if (!certificate?.pic || !certificate?.expirity) {
        return "not_certified";
      }

      if (isDateExpired(certificate.expirity)) {
        return "expired";
      }
      return "approved";
    }

    // Original logic for non-visible users
    // No certificate uploaded
    if (!certificate?.pic || !certificate?.expirity) {
      return "not_certified";
    }

    // Certificate exists - check approval status first
    switch (certificate.status) {
      case "rejected":
        return "rejected";
      case "approved":
        // Check if approved certificate is still valid (not expired)
        if (isDateExpired(certificate.expirity)) {
          return "expired";
        }
        return "approved";
      case "pending":
        return "under_review";
      default:
        // No status means it's uploaded but awaiting approval
        return "under_review";
    }
  };

  // Check if user has a certificate uploaded
  const hasCertificate = () => {
    return certificate?.pic && certificate?.expirity;
  };

  // Check if certificate is approved and valid (not expired)
  const isCertificateValid = () => {
    if (!hasCertificate()) return false;

    // TEMPORARY LOGIC: If user is visible, assume certificate is valid
    // This bypasses the admin approval process for now
    if (user?.visible) {
      return !isDateExpired(certificate.expirity);
    }

    // Original logic for non-visible users
    // Certificate must be approved by admin team
    if (certificate?.status !== "approved") return false;

    return !isDateExpired(certificate.expirity);
  };

  // Check if certificate is under review
  const isCertificateUnderReview = () => {
    // TEMPORARY LOGIC: If user is visible, they are not under review
    if (user?.visible) return false;

    return hasCertificate() && certificate?.status === "pending";
  };

  // Check if certificate is rejected
  const isCertificateRejected = () => {
    // TEMPORARY LOGIC: If user is visible, they are not rejected
    if (user?.visible) return false;

    return hasCertificate() && certificate?.status === "rejected";
  };

  // Check if certificate is expired
  const isCertificateExpired = () => {
    if (!hasCertificate()) return false;

    return isDateExpired(certificate.expirity);
  };

  return {
    status: getCertificationStatus(),
    hasCertificate: hasCertificate(),
    isCertificateValid: isCertificateValid(),
    isCertificateUnderReview: isCertificateUnderReview(),
    isCertificateRejected: isCertificateRejected(),
    isCertificateExpired: isCertificateExpired(),
    certificate
  };
};
